import moment from 'moment';
import { roundHalfToEven } from '../../../website/static/js/services/roundHalfToEven';
import {
  getTrackingUserId,
  identifyTrackingUserId,
} from './identifyTrackingUserId';
import { GDPR_COUNTRY_CODES } from '../constants/countries';

let isTrackingEnabled = false;

export function getIsTrackingEnabled() {
  return isTrackingEnabled;
}

function getMixpanelLibrary() {
  const library = window.mixpanel?.anonymous;

  return library;
}

function processMixpanelMessage(mixpanelMethodName, methodArgs) {
  const library = getMixpanelLibrary();

  if (!library) {
    console.info(
      'tracking: mixpanel',
      methodArgs,
      'skipping: no library for user',
    );
    return;
  }

  // Allow passing the underlying method by name or via a wrapper function.
  const fn = library[mixpanelMethodName];

  if (!fn) {
    // FHCORE-10868 ignore. If users have an adblocker or Mixpanel is never
    // initialized, per Mixpanel documentation some methods won't be accessible.
    // This is okay and expected.
    console.warn(`tracking: mixpanel.${mixpanelMethodName}`, 'invalid method');
    return;
  }

  // Stop slamming mixpanel when running locally.
  console.info(`tracking: mixpanel.${mixpanelMethodName}`, methodArgs);

  if (window.mixpanelConfiguration.isMixpanelPreventNetworkRequestsEnabled) {
    // FHCORE-10470: When isMixpanelPreventNetworkRequestsEnabled is true, tracking services Mixpanel methods will not be properly mapped.
    console.warn('Mixpanel is in debug mode. Methods will not be accessible.');
    return;
  }

  try {
    fn.apply(library, methodArgs);
  } catch (e) {
    console.error(`tracking: mixpanel.${mixpanelMethodName}`, 'exception', e);
  }
}

// Mixpanel methods can be invoked before mixpanel is enabled.
// To prevent loosing data for such method invokations, we use a queue that
// is processed asynchronously. Each method invokation is added
// to the queue together with its parameters.
// The queue is processed asynchronously when mixpanel becomes
// enabled resulting in sending data to the server.
const mixpanelMessageQueue = [];

function processMessageQueue() {
  const CHECK_QUEUE_INTERVAL_MS = 250;

  setInterval(() => {
    if (!isTrackingEnabled || !window.mixpanelConfiguration.isMixpanelEnabled) {
      return;
    }

    while (mixpanelMessageQueue.length > 0) {
      const { mixpanelMethodName, args } = mixpanelMessageQueue.shift();
      processMixpanelMessage(mixpanelMethodName, args);
    }
  }, CHECK_QUEUE_INTERVAL_MS);
}

const pageLoadTime = moment(window.$$loadTime);

function embedTimingProperties() {
  const now = moment();

  const timing = {
    'elapsed-time-seconds': roundHalfToEven(now.diff(pageLoadTime) / 1000, 2),

    // HACK: Mixpanel supports a Date data type but we just want to
    // bucket users into hours of the day, which I don't think it can
    // do with that format.
    'local-time-decimal': parseFloat(now.format('H.mm')),
    'local-day-of-week': now.format('dddd'),

    // NOTE: explicitly NOT UTC.
    'local-date': now.format('YYYY-MM-DDTHH:mm:ss'),
  };

  return timing;
}

function getAllProperties(properties = {}) {
  const data = {
    url: `${window.location.pathname}${window.location.search || ''}`,
    ...embedTimingProperties(),
    ...properties,
  };

  return data;
}

export function track(eventName, properties, callback) {
  const allProperties = getAllProperties(properties);

  mixpanelMessageQueue.push({
    mixpanelMethodName: 'track',
    args: [eventName, allProperties, callback],
  });
}

export function identify(properties) {
  mixpanelMessageQueue.push({
    mixpanelMethodName: 'identify',
    args: [properties],
  });
}

export function addSuperProperty(properties) {
  mixpanelMessageQueue.push({
    mixpanelMethodName: 'register',
    args: [properties],
  });
}

export function loadMixpanelScript() {
  window.mixpanelConfiguration.mixpanelSetup();

  isTrackingEnabled = true;
}

export function enableMixpanel(processorCountry) {
  if (
    GDPR_COUNTRY_CODES.includes(processorCountry) ||
    !window.mixpanelConfiguration.mixpanelSetup
  )
    return;

  loadMixpanelScript();
  identifyTrackingUserId();

  identify(getTrackingUserId());
  addSuperProperty({
    distinct_id: getTrackingUserId(),
    userType: 'anonymous',
  });

  processMessageQueue();
}
